// src/components/profile/roi/KeyMetricsGrid.jsx
import React from 'react';
import { DollarSign, TrendingUp, Clock, Percent, Users, RefreshCw } from 'lucide-react';

export default function KeyMetricsGrid({ metrics }) {
  if (!metrics) return null;
  
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '$0';
    const absValue = Math.abs(value);
    if (absValue >= 1000000) return `$${(value/1000000).toFixed(1)}M`;
    if (absValue >= 1000) return `$${(value/1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };
  
  const formatPercent = (value) => {
    if (!value || isNaN(value)) return '0%';
    return `${Math.round(value)}%`;
  };
  
  // Build cards with actual metrics
  const cards = [
    {
      icon: TrendingUp,
      label: 'Year 1 Benefit',
      value: formatCurrency(metrics.benefitY1),
      subtext: 'Revenue + savings',
      color: 'text-green-600'
    },
    {
      icon: RefreshCw,
      label: 'Investment',
      value: formatCurrency(metrics.investmentY1),
      subtext: 'Total program cost',
      color: 'text-blue-600'
    },
    {
      icon: DollarSign,
      label: 'Net Value',
      value: formatCurrency(metrics.netY1),
      subtext: 'Year 1 net benefit',
      color: metrics.netY1 > 0 ? 'text-green-600' : 'text-red-600'
    },
    {
      icon: TrendingUp,
      label: 'ROI',
      value: formatPercent(metrics.roiPct),
      subtext: metrics.scenario === 'base' ? 'Base scenario' : `${metrics.scenario} scenario`,
      color: metrics.roiPct > 150 ? 'text-green-600' : metrics.roiPct > 80 ? 'text-yellow-600' : 'text-red-600'
    },
    {
      icon: Clock,
      label: 'Payback',
      value: metrics.paybackMonths ? `${metrics.paybackMonths} mo` : 'No payback',
      subtext: metrics.paybackMonths ? 'Time to break even' : 'Investment not recovered',
      color: metrics.paybackMonths && metrics.paybackMonths <= 12 ? 'text-green-600' : 'text-yellow-600'
    },
    {
      icon: Percent,
      label: 'Confidence',
      value: formatPercent((metrics.confidence || 0.75) * 100),
      subtext: 'Model confidence',
      color: metrics.confidence >= 0.8 ? 'text-green-600' : metrics.confidence >= 0.6 ? 'text-yellow-600' : 'text-red-600'
    }
  ];
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      {cards.map((card, idx) => {
        const Icon = card.icon;
        return (
          <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-2">
              <Icon className={`w-5 h-5 ${card.color}`} />
              {/* Add scenario indicator for variable metrics */}
              {(card.label === 'Year 1 Benefit' || card.label === 'Net Value' || card.label === 'ROI') && metrics.scenario !== 'base' && (
                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                  {metrics.scenario === 'conservative' ? '0.7×' : '1.3×'}
                </span>
              )}
            </div>
            <div className={`text-2xl font-bold ${card.color} mb-1`}>
              {card.value}
            </div>
            <div className="text-xs font-medium text-gray-700">
              {card.label}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {card.subtext}
            </div>
          </div>
        );
      })}
    </div>
  );
}